import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TextInput } from 'react-native'
import { PaymentCardInformation } from '@/src/app/main/(tabs)/card/card-management/_common/paymentCardInformation'
import { OptionItemSelector } from '@/src/app/main/(tabs)/card/card-management/payment/optionItemSelector'
import { formatCurrency } from '@/src/common/utils/general'
import { fontFamilies } from '@/src/common/theme'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'

export type PaymentType =
  | 'Minimum Payment'
  | 'Statement Balance'
  | 'Current Balance'
  | 'Other Amount'

export function PaymentAmountSection({
  minimumPayment,
  statementBalance,
  currentBalance,
  onAmountChange,
  selectedPaymentType,
  onPaymentTypeSelectionChange,
  paymentAmount,
}: {
  paymentAmount: string | undefined
  minimumPayment: string | undefined
  statementBalance: string | undefined
  currentBalance: string | undefined
  onAmountChange: (value: string | undefined) => void
  selectedPaymentType: PaymentType
  onPaymentTypeSelectionChange: (value: PaymentType) => void
}) {
  const [otherAmount, setOtherAmount] = useState<string | undefined>(
    paymentAmount && selectedPaymentType === 'Other Amount' ? paymentAmount : undefined,
  )
  const inputRef = useRef<TextInput>(null)
  const isFirstRender = useRef(true)

  const handleAmountChange = useCallback(() => {
    switch (selectedPaymentType) {
      case 'Minimum Payment':
        onAmountChange(minimumPayment)
        break
      case 'Statement Balance':
        onAmountChange(statementBalance)
        break
      case 'Current Balance':
        onAmountChange(currentBalance)
        break
      case 'Other Amount':
        onAmountChange(otherAmount != null ? otherAmount.replace(/[^0-9]/g, '') : otherAmount)
        break
    }
  }, [
    onAmountChange,
    selectedPaymentType,
    minimumPayment,
    statementBalance,
    currentBalance,
    otherAmount,
  ])

  useEffect(() => {
    handleAmountChange()
  }, [handleAmountChange])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    } else if (selectedPaymentType === 'Other Amount' && inputRef.current !== null) {
      inputRef.current.focus()
    }
  }, [selectedPaymentType, inputRef])

  const inputValue = useMemo(() => {
    if (otherAmount !== undefined) {
      return formatCurrency(otherAmount)
    }
  }, [otherAmount])

  const Input = useMemo(
    () => (
      <TextInput
        ref={inputRef}
        value={inputValue}
        onChange={(value) => {
          const rawValue = value.nativeEvent.text.replace(/[^0-9]/g, '')
          setOtherAmount(rawValue)
        }}
        placeholder="Enter amount"
        keyboardType={'numeric'}
        returnKeyType="done"
        style={{
          fontFamily: fontFamilies['PlusJakartaSans-Regular'],
          fontWeight: 400,
          fontSize: 16,
        }}
        onPress={() => onPaymentTypeSelectionChange('Other Amount')}
        testID={CardPaymentsE2E.Ids.Press.OtherAmount}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputRef, otherAmount, onPaymentTypeSelectionChange, formatCurrency],
  )

  const paymentAmountOptions = useMemo(
    () =>
      [
        {
          type: 'Minimum Payment',
          title: minimumPayment ? formatCurrency(minimumPayment) : 'N/A',
        },
        {
          type: 'Statement Balance',
          title: statementBalance ? formatCurrency(statementBalance) : 'N/A',
        },
        {
          type: 'Current Balance',
          title: currentBalance ? formatCurrency(currentBalance) : 'N/A',
        },
        {
          type: 'Other Amount',
          title: Input,
          withBorder: false,
        },
      ] as const satisfies { type: PaymentType; title: string | ReactNode; withBorder?: boolean }[],
    [minimumPayment, statementBalance, currentBalance, Input],
  )

  return (
    <PaymentCardInformation title="Payment Amount" containerStyle={{ marginHorizontal: 16 }}>
      {paymentAmountOptions.map((item) => (
        <OptionItemSelector
          onItemPress={() => onPaymentTypeSelectionChange(item.type)}
          key={item.type}
          label={item.type}
          title={item.title}
          isSelected={selectedPaymentType === item.type}
          withBorder={'withBorder' in item ? item.withBorder : undefined}
        />
      ))}
    </PaymentCardInformation>
  )
}
